import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import useThemeMode from "../../hooks/useThemeMode";
import { getMarketingTheme } from "../../utils/marketingTheme";
import useI18n from "../../i18n/useI18n";

const AboutHero = () => {
  const { isDark } = useThemeMode();
  const { t } = useI18n();
  const colors = useMemo(() => getMarketingTheme(isDark), [isDark]);

  const tt = (key, fallback) => {
    const value = t(key);
    return value === key ? fallback : value;
  };

  const heroPoints = useMemo(
    () => [
      tt("about.cards.builtForCreators", "Built for creators who want less friction"),
      tt("about.cards.keepsOutputMoving", "Designed to keep content output moving"),
      tt(
        "about.cards.oneWorkflow",
        "Made to connect hooks, titles, captions, and scripts in one workflow"
      ),
      tt("about.cards.globalCreators", "Useful for creators in Nigeria and beyond"),
    ],
    [t]
  );

  return (
    <section
      style={{
        padding: "84px 20px 40px",
        background: colors.heroBg,
      }}
    >
      <div
        style={{
          maxWidth: "1200px",
          margin: "0 auto",
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
          gap: "32px",
          alignItems: "center",
        }}
      >
        <div>
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "8px",
              padding: "8px 14px",
              borderRadius: "999px",
              background: colors.badgeBg,
              color: colors.badgeText,
              fontWeight: "700",
              fontSize: "12px",
              marginBottom: "18px",
              border: isDark ? `1px solid ${colors.border}` : "none",
            }}
          >
            {tt("about.heroBadge", "About Hookora")}
          </div>

          <h1
            style={{
              fontSize: "clamp(2.4rem, 5vw, 4.2rem)",
              lineHeight: "1.04",
              margin: "0 0 16px",
              color: colors.text,
              letterSpacing: "-0.03em",
            }}
          >
            {tt(
              "about.heroTitle",
              "Hookora helps creators move from idea to better content faster"
            )}
          </h1>

          <p
            style={{
              maxWidth: "760px",
              margin: "0 0 24px",
              fontSize: "17px",
              lineHeight: "1.75",
              color: colors.textSoft,
            }}
          >
            {tt(
              "about.heroDescription",
              "Hookora is an AI workflow platform built for creators who want a cleaner, faster, and more confident way to generate hooks, titles, captions, hashtags, descriptions, scripts, and thumbnail text."
            )}
          </p>

          <div
            style={{
              display: "flex",
              gap: "12px",
              flexWrap: "wrap",
            }}
          >
            <Link
              to="/register"
              style={{
                textDecoration: "none",
                background: "linear-gradient(135deg, #2563eb, #7c3aed)",
                color: "#fff",
                padding: "14px 18px",
                borderRadius: "12px",
                fontWeight: "800",
                boxShadow: "0 12px 28px rgba(79, 70, 229, 0.24)",
              }}
            >
              {tt("about.createFreeAccount", "Create Free Account")}
            </Link>

            <Link
              to="/features"
              style={{
                textDecoration: "none",
                background: colors.panelBg,
                color: colors.text,
                padding: "14px 18px",
                borderRadius: "12px",
                fontWeight: "800",
                border: `1px solid ${colors.borderStrong}`,
              }}
            >
              {tt("about.exploreFeatures", "Explore Features")}
            </Link>
          </div>
        </div>

        <div>
          <div
            style={{
              background: colors.cardBg,
              border: `1px solid ${colors.border}`,
              borderRadius: "26px",
              padding: "22px",
              boxShadow: colors.shadow,
              backdropFilter: isDark ? "blur(14px)" : "none",
            }}
          >
            <div style={{ display: "grid", gap: "12px" }}>
              {heroPoints.map((item) => (
                <div
                  key={item}
                  style={{
                    background: colors.cardBgSoft,
                    border: `1px solid ${colors.border}`,
                    borderRadius: "16px",
                    padding: "14px 16px",
                    color: colors.text,
                    lineHeight: "1.6",
                    fontWeight: "600",
                  }}
                >
                  {item}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AboutHero;